import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import apiService from '../services/api.service';
import SapApprovalEmailTemplates from '../components/sapApproval/SapApprovalEmailTemplates';
import SapUserLinks from '../components/sapApproval/SapUserLinks';
import SapUserPasswords from '../components/sapApproval/SapUserPasswords';

/**
 * Settings > SAP Approval Workflows (Admin). Read-only view of a company's SAP B1
 * approval procedures (templates, originators, ordered stages and their approvers),
 * its pending PO approval requests, and the e-mails / decisions PRPO handled for it;
 * plus the SAP B1 passwords of the PRPO users (SAP B1 passwords tab) and which PRPO user
 * each SAP user belongs to (SAP users tab).
 */

interface SapUser { id: number; code: string; name: string; email: string; emailSource: string; locked: boolean }

const userChip = (u: SapUser) => (
  <Tooltip
    key={`${u.id}-${u.code}`}
    title={u.email ? `${u.code} · ${u.email}${u.emailSource === 'PRPO' ? ' (from PRPO user)' : ''}` : `${u.code} · no e-mail in SAP or PRPO`}
  >
    <Chip
      size="small"
      label={u.name || u.code}
      color={u.email ? 'default' : 'warning'}
      variant={u.email ? 'outlined' : 'filled'}
      sx={{ mr: 0.5, mb: 0.5 }}
    />
  </Tooltip>
);

const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' | 'info' =>
  s === 'Sent' || s === 'Done' ? 'success' : s === 'NoEmail' ? 'warning' : s === 'Failed' ? 'error' : s === 'Sending' ? 'info' : 'default';

const today = () => new Date().toISOString().slice(0, 10);

const SapApprovalWorkflows: React.FC = () => {
  const [companies, setCompanies] = React.useState<any[]>([]);
  const [companyId, setCompanyId] = React.useState<number | ''>('');
  const [tab, setTab] = React.useState(0);
  const [error, setError] = React.useState('');

  const [workflows, setWorkflows] = React.useState<any | null>(null);
  const [wfLoading, setWfLoading] = React.useState(false);
  const [poOnly, setPoOnly] = React.useState(true);
  const [activeOnly, setActiveOnly] = React.useState(true);

  const [requests, setRequests] = React.useState<any | null>(null);
  const [reqLoading, setReqLoading] = React.useState(false);
  const [since, setSince] = React.useState('');
  const [reqStatus, setReqStatus] = React.useState<'pending' | 'all'>('pending');
  const [pollResult, setPollResult] = React.useState<any | null>(null);
  const [polling, setPolling] = React.useState(false);

  const [log, setLog] = React.useState<any | null>(null);
  const [logLoading, setLogLoading] = React.useState(false);
  const [logBusy, setLogBusy] = React.useState('');
  const [logMsg, setLogMsg] = React.useState<{ severity: 'success' | 'info' | 'warning' | 'error'; text: string } | null>(null);
  const [logType, setLogType] = React.useState<'all' | 'Approval' | 'Outcome' | 'Accounts'>('all');

  React.useEffect(() => {
    (async () => {
      try {
        const res: any = await apiService.getCompanies(true);
        const list = res?.data || [];
        setCompanies(list);
        const preferred = Number(localStorage.getItem('selectedCompanyId') || 0);
        const pick = list.find((c: any) => Number(c.companyid) === preferred) || list[0];
        if (pick) setCompanyId(Number(pick.companyid));
      } catch (e: any) {
        setError(String(e?.response?.data?.message || 'Could not load companies'));
      }
    })();
  }, []);

  const company = companies.find((c) => Number(c.companyid) === Number(companyId));
  // Ignore answers that arrive after the user picked another company.
  const currentId = React.useRef<number | ''>('');
  currentId.current = companyId;
  const isCurrent = (id: number) => Number(currentId.current) === id;

  const loadWorkflows = React.useCallback(async (id: number, refresh = false) => {
    setWfLoading(true); setError(''); setWorkflows(null);
    try {
      const res: any = await apiService.getSapApprovalWorkflows(id, refresh);
      if (isCurrent(id)) setWorkflows(res?.data || null);
    } catch (e: any) {
      setError(String(e?.response?.data?.message || 'Could not read the SAP approval procedures'));
    } finally {
      setWfLoading(false);
    }
  }, []);

  const loadRequests = React.useCallback(async (id: number, s: string, st: 'pending' | 'all') => {
    setReqLoading(true); setError('');
    try {
      const res: any = await apiService.getSapApprovalRequests(id, { since: s || undefined, status: st });
      if (!isCurrent(id)) return;
      setRequests(res?.data || null);
      if (!s && res?.data?.since) setSince(res.data.since);
    } catch (e: any) {
      setError(String(e?.response?.data?.message || 'Could not read the SAP approval requests'));
    } finally {
      setReqLoading(false);
    }
  }, []);

  const loadLog = React.useCallback(async (id: number) => {
    setLogLoading(true); setError('');
    try {
      const res: any = await apiService.getSapApprovalLog(id);
      if (isCurrent(id)) setLog(res?.data || null);
    } catch (e: any) {
      setError(String(e?.response?.data?.message || 'Could not read the e-mail log'));
    } finally {
      setLogLoading(false);
    }
  }, []);

  React.useEffect(() => {
    // A new company starts clean; the effect below loads the open tab.
    setWorkflows(null); setRequests(null); setLog(null); setPollResult(null); setSince('');
  }, [companyId]);

  React.useEffect(() => {
    if (!companyId) return;
    if (tab === 0 && !workflows && !wfLoading) void loadWorkflows(Number(companyId));
    if (tab === 1 && !requests && !reqLoading) void loadRequests(Number(companyId), since, reqStatus);
    if (tab === 2 && !log && !logLoading) void loadLog(Number(companyId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, companyId, workflows, requests, log]);

  const runPoll = async () => {
    if (!companyId) return;
    setPolling(true); setPollResult(null); setError('');
    try {
      const res: any = await apiService.pollSapApprovals(Number(companyId));
      setPollResult(res?.data || null);
      await loadRequests(Number(companyId), since, reqStatus);
    } catch (e: any) {
      setError(String(e?.response?.data?.message || 'Could not check SAP'));
    } finally {
      setPolling(false);
    }
  };

  const pollText = (p: any) => (p?.skipped
    ? `${p.skipped}. The next automatic check (every 2 minutes) picks the e-mails up.`
    : `${p?.approvalEmails || 0} approval e-mail(s) and ${p?.outcomeEmails || 0} outcome e-mail(s) sent, ${p?.failed || 0} failed, ${p?.noEmail || 0} without e-mail address.`);

  const retry = async (notificationId?: number) => {
    if (!companyId) return;
    setLogBusy(notificationId ? `retry-${notificationId}` : 'retry'); setLogMsg(null);
    try {
      const res: any = await apiService.retrySapApprovals(Number(companyId), notificationId);
      const d = res?.data || {};
      setLogMsg({ severity: d.poll?.failed ? 'warning' : 'success', text: `${d.queued || 0} e-mail(s) retried. ${pollText(d.poll)}` });
    } catch (e: any) {
      setLogMsg({ severity: 'error', text: String(e?.response?.data?.message || 'Could not retry') });
    } finally {
      setLogBusy('');
      await loadLog(Number(companyId));
    }
  };

  // Accounts e-mails (Settings › Accounts notifications) of a failed send: try that one again now.
  const resendAccounts = async (notificationId: number) => {
    setLogBusy(`acc-${notificationId}`); setLogMsg(null);
    try {
      const res: any = await apiService.resendAccountsNotification(notificationId);
      const d = res?.data || {};
      setLogMsg({ severity: d.failed ? 'warning' : 'success', text: d.sent ? 'Accounts e-mail sent again.' : d.failed ? 'The mail server refused it again; see the reason.' : String(d.skipped || 'Nothing to send.') });
    } catch (e: any) {
      setLogMsg({ severity: 'error', text: String(e?.response?.data?.message || 'Could not send it again') });
    } finally {
      setLogBusy('');
      if (companyId) await loadLog(Number(companyId));
    }
  };

  // Approval and outcome e-mails with the accounts e-mails of the same company, newest first.
  const logRows: any[] = log
    ? [
      ...(log.notifications || []).map((n: any) => ({ ...n, rowKey: `n-${n.notificationid}`, type: n.kind })),
      ...(log.accounts || []).map((a: any) => ({ ...a, rowKey: `a-${a.notificationid}`, type: 'Accounts' })),
    ]
      .filter((r) => logType === 'all' || r.type === logType)
      .sort((a, b) => new Date(b.lastattempt).getTime() - new Date(a.lastattempt).getTime())
    : [];

  const sendTest = async () => {
    if (!companyId) return;
    setLogBusy('test'); setLogMsg(null);
    try {
      const res: any = await apiService.sendSapApprovalTestEmail(Number(companyId));
      setLogMsg({ severity: 'success', text: `Test e-mail sent to ${res?.data?.to}. The mail server accepts PRPO's e-mails.` });
    } catch (e: any) {
      setLogMsg({ severity: 'error', text: String(e?.response?.data?.message || 'The test e-mail could not be sent') });
    } finally {
      setLogBusy('');
    }
  };

  const templates = (workflows?.templates || []).filter((t: any) =>
    (!activeOnly || t.active) && (!poOnly || (t.documents || []).some((d: any) => d.code === 'atdtPurchaseOrder')));

  const sapMode = String(company?.poapprovalsource || 'PRPO').toUpperCase() === 'SAP';

  return (
    <Box component="section" aria-labelledby="sap-approval-heading" sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} mb={2}>
        <Box>
          <Typography component="h1" id="sap-approval-heading" variant="h5" sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}>
            SAP Approval Workflows
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The approval procedures defined in SAP B1 (Administration › Approval Procedures), read live from the selected company.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 360 } }}>
          <InputLabel id="sap-approval-company">Company</InputLabel>
          <Select
            labelId="sap-approval-company"
            label="Company"
            value={companyId}
            onChange={(e) => setCompanyId(Number(e.target.value))}
          >
            {companies.map((c) => (
              <MenuItem key={c.companyid} value={Number(c.companyid)}>
                {c.companyname} ({c.databasename})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {company ? (
        <Alert severity={sapMode ? (company.sapapprovalreviewemail ? 'warning' : 'success') : 'info'} sx={{ mb: 2 }}>
          {sapMode
            ? company.sapapprovalreviewemail
              ? <>PO approval follows SAP B1 from <strong>{String(company.sapapprovalstartdate || '').slice(0, 10)}</strong> in <strong>review mode</strong>: every approval e-mail goes to {company.sapapprovalreviewemail} only, and decisions from e-mail links are disabled.</>
              : <>PO approval follows SAP B1 from <strong>{String(company.sapapprovalstartdate || '').slice(0, 10)}</strong>: PRPO e-mails each approver and records their decisions in SAP.</>
            : <>PO approvals of this company are not e-mailed from SAP (Company Management › PO approval = PRPO workflow). This page still shows its SAP procedures.</>}
        </Alert>
      ) : null}

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Approval procedures" />
        <Tab label="Pending approvals" />
        <Tab label="E-mail log" />
        <Tab label="E-mail templates" />
        <Tab label="SAP B1 passwords" />
        <Tab label="SAP users" />
      </Tabs>

      {tab === 0 ? (
        <Box>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center" mb={1.5}>
            <FormControlLabel control={<Switch checked={poOnly} onChange={(e) => setPoOnly(e.target.checked)} />} label="Purchase orders only" />
            <FormControlLabel control={<Switch checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />} label="Active only" />
            <Button size="small" onClick={() => companyId && loadWorkflows(Number(companyId), true)} disabled={wfLoading}>Refresh from SAP</Button>
            {workflows ? (
              <Typography variant="body2" color="text.secondary">
                {templates.length} of {workflows.templates.length} templates{workflows.cachedAt ? ` · read from SAP at ${new Date(workflows.cachedAt).toLocaleTimeString()}` : ''}
              </Typography>
            ) : null}
          </Stack>
          {wfLoading ? (
            <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={20} /><Typography variant="body2">Reading SAP…</Typography></Stack>
          ) : null}
          {!wfLoading && workflows && templates.length === 0 ? (
            <Alert severity="info">No approval procedure matches these filters in this company.</Alert>
          ) : null}
          {templates.map((t: any) => (
            <Accordion key={t.code} disableGutters TransitionProps={{ unmountOnExit: true }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ width: '100%' }}>
                  <Typography sx={{ fontWeight: 700 }}>{t.name}</Typography>
                  <Chip size="small" label={t.active ? 'Active' : 'Inactive'} color={t.active ? 'success' : 'default'} />
                  <Typography variant="body2" color="text.secondary">
                    {t.stages.length} stage{t.stages.length === 1 ? '' : 's'} · {t.documents.map((d: any) => d.label).join(', ') || 'no documents'}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {t.remarks ? <Typography variant="body2" color="text.secondary">{t.remarks}</Typography> : null}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Originators (who triggers it)</Typography>
                    <Box>{t.originators.length ? t.originators.map(userChip) : <Typography variant="body2" color="text.secondary">None</Typography>}</Box>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Stages, in approval order</Typography>
                    <Stack spacing={1}>
                      {t.stages.map((s: any, i: number) => (
                        <Paper key={`${s.code}-${i}`} variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {i + 1}. {s.name}
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' '}· {s.approvalsRequired} approval{s.approvalsRequired === 1 ? '' : 's'} required{s.remarks ? ` · ${s.remarks}` : ''}
                            </Typography>
                          </Typography>
                          <Box mt={0.5}>{s.approvers.map(userChip)}</Box>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Conditions</Typography>
                    {!t.useTerms ? (
                      <Typography variant="body2">Always – every document of these types by an originator needs approval.</Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {t.terms.map((term: any, i: number) => (
                          <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {Object.entries(term).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                          </Typography>
                        ))}
                        {t.queries.map((q: any) => (
                          <Typography key={q.id} variant="body2">SAP query #{q.id}{q.name ? ` – ${q.name}` : ''}</Typography>
                        ))}
                        {!t.terms.length && !t.queries.length ? <Typography variant="body2" color="text.secondary">No condition rows returned by SAP.</Typography> : null}
                      </Stack>
                    )}
                    {t.activeWhenUpdating ? <Typography variant="caption" color="text.secondary">Also applies when documents are updated.</Typography> : null}
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
          <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
            Orange approvers have no e-mail in SAP (Administration › Setup › General › Users › E-Mail) and no PRPO user with their SAP user code.
          </Typography>
        </Box>
      ) : null}

      {tab === 1 ? (
        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} mb={1.5}>
            <TextField size="small" type="date" label="Created since" value={since} onChange={(e) => setSince(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ max: today() }} />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="sap-req-status">Status</InputLabel>
              <Select labelId="sap-req-status" label="Status" value={reqStatus} onChange={(e) => setReqStatus(e.target.value as 'pending' | 'all')}>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => companyId && loadRequests(Number(companyId), since, reqStatus)} disabled={reqLoading}>Show</Button>
            {sapMode ? (
              <Button variant="contained" onClick={runPoll} disabled={polling}>{polling ? 'Checking SAP…' : 'Check SAP and send e-mails now'}</Button>
            ) : null}
          </Stack>
          {pollResult ? (
            <Alert severity={pollResult.skipped ? 'info' : pollResult.failed ? 'warning' : 'success'} sx={{ mb: 1.5 }}>
              {pollResult.skipped
                ? pollResult.skipped
                : `${pollResult.pendingRequests} pending request(s) · ${pollResult.approvalEmails} approval e-mail(s) and ${pollResult.outcomeEmails} outcome e-mail(s) sent · ${pollResult.noEmail} without e-mail · ${pollResult.failed} failed`}
            </Alert>
          ) : null}
          {reqLoading ? (
            <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={20} /><Typography variant="body2">Reading SAP…</Typography></Stack>
          ) : null}
          {requests && !reqLoading ? (
            <>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {requests.total} purchase order approval request(s) since {requests.since}{requests.total > requests.shown ? ` – showing the latest ${requests.shown}` : ''}.
              </Typography>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="SAP approval requests">
                  <TableHead>
                    <TableRow>
                      <TableCell>Request</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Draft no.</TableCell>
                      <TableCell>Draft key</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Raised by</TableCell>
                      <TableCell>Status / stage</TableCell>
                      <TableCell>Waiting for</TableCell>
                      <TableCell>PRPO e-mails</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.requests.map((r: any) => (
                      <TableRow key={r.code} hover>
                        <TableCell>{r.code}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.createdDate}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.docNum || '-'}</TableCell>
                        <TableCell>{r.draftEntry || '-'}</TableCell>
                        <TableCell>{r.vendor || '-'}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{r.total !== null ? `${r.currency} ${Number(r.total).toFixed(2)}` : '-'}</TableCell>
                        <TableCell>{r.originator}</TableCell>
                        <TableCell>{r.outcome || r.currentStageName}</TableCell>
                        <TableCell>
                          {r.pendingApprovers.map((u: any) => (
                            <Chip key={u.id} size="small" sx={{ mr: 0.5, mb: 0.5 }} label={u.name} color={u.email ? 'default' : 'warning'} variant="outlined" title={u.email || 'no e-mail'} />
                          ))}
                        </TableCell>
                        <TableCell>
                          {r.notifications.length ? r.notifications.map((n: any, i: number) => (
                            <Box key={i} sx={{ mb: 0.5 }}>
                              <Chip
                                size="small"
                                color={statusColor(n.status)}
                                label={`${n.kind === 'Outcome' ? 'Outcome' : 'Approval'}${n.reviewmode ? ' (review)' : ''}: ${n.status}`}
                                title={[n.recipientemail, n.error].filter(Boolean).join(' – ')}
                              />
                              {n.error && (n.status === 'Failed' || n.status === 'NoEmail') ? (
                                <Typography variant="caption" display="block" color={n.status === 'Failed' ? 'error' : 'warning.main'} sx={{ maxWidth: 320, wordBreak: 'break-word' }}>
                                  {n.error}
                                </Typography>
                              ) : null}
                            </Box>
                          )) : <Typography variant="caption" color="text.secondary">none</Typography>}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!requests.requests.length ? (
                      <TableRow><TableCell colSpan={10}><Typography variant="body2" color="text.secondary">No requests.</Typography></TableCell></TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </Box>
      ) : null}

      {tab === 2 ? (
        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} mb={1}>
            <Button size="small" onClick={() => companyId && loadLog(Number(companyId))} disabled={logLoading}>Refresh</Button>
            <Button size="small" variant="contained" onClick={() => retry()} disabled={!!logBusy || !sapMode}>
              {logBusy === 'retry' ? 'Retrying…' : 'Retry failed e-mails now'}
            </Button>
            <Button size="small" variant="outlined" onClick={sendTest} disabled={!!logBusy}>
              {logBusy === 'test' ? 'Sending…' : 'Send test e-mail'}
            </Button>
            {logLoading ? <CircularProgress size={20} aria-label="Loading the e-mail log" /> : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Failed e-mails are retried automatically every 30 minutes while SAP still waits for that approver. The test e-mail goes to the
            review addresses (or to you) through the same mail server, and shows the mail server&apos;s answer. Accounts rows are the
            e-mails to the accounts team when a PO is finally approved (Settings › Accounts notifications).
          </Typography>
          <FormControl size="small" sx={{ minWidth: 220, mb: 1.5 }}>
            <InputLabel id="sap-log-type">Type</InputLabel>
            <Select labelId="sap-log-type" label="Type" value={logType} onChange={(e) => setLogType(e.target.value as any)}>
              <MenuItem value="all">All types</MenuItem>
              <MenuItem value="Approval">Approval requests</MenuItem>
              <MenuItem value="Outcome">Outcomes</MenuItem>
              <MenuItem value="Accounts">Accounts notifications</MenuItem>
            </Select>
          </FormControl>
          {logMsg ? <Alert severity={logMsg.severity} sx={{ mb: 1.5 }} onClose={() => setLogMsg(null)}>{logMsg.text}</Alert> : null}
          {log ? (
            <Stack spacing={3}>
              <Box>
                <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>E-mails</Typography>
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="Approval e-mails">
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell>
                        <TableCell>Request</TableCell>
                        <TableCell>Draft no.</TableCell>
                        <TableCell>Draft key</TableCell>
                        <TableCell>Kind</TableCell>
                        <TableCell>SAP user</TableCell>
                        <TableCell>Sent to</TableCell>
                        <TableCell>Attachments</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logRows.map((n: any) => (n.type === 'Accounts' ? (
                        <TableRow key={n.rowKey} sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(n.lastattempt).toLocaleString()}</TableCell>
                          <TableCell>{n.requestcode || '-'}</TableCell>
                          <TableCell>{n.source === 'SAP' && n.docentry ? `PO ${n.docnum}` : n.source === 'PRPO' ? `PO ${n.docnum || n.poid}` : (n.docnum || '-')}</TableCell>
                          <TableCell>{n.draftentry || '-'}</TableCell>
                          <TableCell>
                            <Chip size="small" color="primary" variant="outlined" label="Accounts" />
                            {n.reviewmode ? <Typography variant="caption" display="block">(review)</Typography> : null}
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell sx={{ wordBreak: 'break-word' }}>
                            {n.recipientemail}
                            {n.sentto && n.sentto !== n.recipientemail ? <Typography variant="caption" display="block" color="text.secondary">sent to {n.sentto}</Typography> : null}
                          </TableCell>
                          <TableCell>{n.attachmentcount ? `${n.attachmentsincluded}/${n.attachmentcount}` : '-'}</TableCell>
                          <TableCell>
                            <Chip size="small" color={statusColor(n.status)} label={n.status} />
                            {n.attempts > 1 ? <Typography variant="caption" display="block" color="text.secondary">{n.attempts} attempts</Typography> : null}
                          </TableCell>
                          <TableCell sx={{ minWidth: 240, maxWidth: 480 }}>
                            <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                              Rule: {n.rules || '-'}{n.paymentterms ? ` · ${n.paymentterms}` : ''}
                            </Typography>
                            {n.error ? <Typography variant="caption" color={n.status === 'Failed' ? 'error' : 'text.secondary'} sx={{ wordBreak: 'break-word' }}>{n.error}</Typography> : null}
                          </TableCell>
                          <TableCell>
                            {n.status === 'Failed' ? (
                              <Button size="small" onClick={() => resendAccounts(Number(n.notificationid))} disabled={!!logBusy}>
                                {logBusy === `acc-${n.notificationid}` ? 'Sending…' : 'Send again'}
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow key={n.rowKey}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(n.lastattempt).toLocaleString()}</TableCell>
                          <TableCell>{n.requestcode}</TableCell>
                          <TableCell>{n.docnum || '-'}</TableCell>
                          <TableCell>{n.draftentry || '-'}</TableCell>
                          <TableCell>{n.kind}{n.outcome ? ` – ${n.outcome}` : ''}{n.reviewmode ? ' (review)' : ''}</TableCell>
                          <TableCell>{n.sapusercode || n.sapuserid}</TableCell>
                          <TableCell>{n.recipientemail || '-'}</TableCell>
                          <TableCell>{n.attachmentcount ? `${n.attachmentsincluded}/${n.attachmentcount}` : '-'}</TableCell>
                          <TableCell>
                            <Chip size="small" color={statusColor(n.status)} label={n.status} />
                            {n.attempts > 1 ? <Typography variant="caption" display="block" color="text.secondary">{n.attempts} attempts</Typography> : null}
                          </TableCell>
                          <TableCell sx={{ minWidth: 240, maxWidth: 480 }}>
                            {n.error ? (
                              <Typography variant="caption" color={n.status === 'Failed' ? 'error' : n.status === 'NoEmail' ? 'warning.main' : 'text.secondary'} sx={{ wordBreak: 'break-word' }}>
                                {n.error}
                              </Typography>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {n.status === 'Failed' || n.status === 'NoEmail' ? (
                              <Button size="small" onClick={() => retry(Number(n.notificationid))} disabled={!!logBusy || !sapMode}>
                                {logBusy === `retry-${n.notificationid}` ? 'Retrying…' : 'Retry'}
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      )))}
                      {!logRows.length ? <TableRow><TableCell colSpan={11}>No e-mails yet.</TableCell></TableRow> : null}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box>
                <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Decisions made from e-mail links</Typography>
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="Approval decisions">
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell>
                        <TableCell>Request</TableCell>
                        <TableCell>SAP user</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Remarks</TableCell>
                        <TableCell>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {log.decisions.map((d: any) => (
                        <TableRow key={d.decisionid}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(d.decideddate).toLocaleString()}</TableCell>
                          <TableCell>{d.requestcode}</TableCell>
                          <TableCell>{d.sapusercode}</TableCell>
                          <TableCell>{d.action}</TableCell>
                          <TableCell>{d.remarks}</TableCell>
                          <TableCell><Chip size="small" color={statusColor(d.status)} label={d.status} title={d.error || ''} /></TableCell>
                        </TableRow>
                      ))}
                      {!log.decisions.length ? <TableRow><TableCell colSpan={6}>No decisions yet.</TableCell></TableRow> : null}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          ) : null}
        </Box>
      ) : null}

      {tab === 3 ? <SapApprovalEmailTemplates companyId={companyId} companyName={company?.companyname || ''} /> : null}

      {tab === 5 ? <SapUserLinks companyId={companyId} companyName={company?.companyname || ''} /> : null}

      {tab === 4 ? <SapUserPasswords /> : null}
    </Box>
  );
};

export default SapApprovalWorkflows;
